// src/pages/DataQuality/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components (Our reusable components)
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import PlotContainer from "../../Components/Common/PlotContainer";
import QCSummaryDashboard from "./components/QCSummaryDashboard";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ActionButton from "../../Components/Common/ActionButton";

// Data Quality specific components
import PlotTypeSelector from "./components/PlotTypeSelector";
import PlotOptions from "./components/PlotOptions";

// API
import {
  getDataQualityPlotData,
  generateDataQualityPlot,
  getPlusStatus,
} from "../../helpers/eirna_backend_helper";

// Hooks (Enhanced with our reusable patterns)
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { EnterButtonListener} from "../../hooks/EnterButtonListener";
import { useDefaultFileSelection } from "../../hooks/useDefaultFileSelection";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useDataQualityShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

// Constants
const PLOT_TYPES = [
  {
    id: "trip_periodicity",
    label: "Triplet Periodicity",
    icon: "📊",
    description:
      "Shows the triplet periodicity across a given range of readlengths",
  },
  {
    id: "readlen_dist",
    label: "Read Length Distribution",
    icon: "📏",
    description: "Shows different read lengths and their counts",
  },
  {
    id: "mrna_dist_readlen",
    label: "Read length distribution across RNA region",
    icon: "📊",
    description:
      "Line chart showing counts by region, broken down by read length",
  },
  {
    id: "metagene_plot",
    label: "Metagene Profile",
    icon: "📈",
    description:
      "Shows counts relative to sequence features like start/stop codons",
  },
  {
    id: "custom_metagene",
    label: "Custom Metagene Profile",
    icon: "📈",
    description:
      "Shows counts relative to user defined sequence features",
  },
  {
    id: "heatmap",
    label: "Heatmap",
    icon: "🔥",
    description: "Shows readlengths vs position relative to start/stop codon",
  },
  {
    id: "cluster",
    label: "Sample clustering",
    icon: "🌐",
    description: "Hierarchical clustering of samples",
  },
  {
    id: "replicate_comp",
    label: "Correlation analysis",
    icon: "🔗",
    description: "Maps counts for every transcript from two different files",
  },  
  {
    id: "te",
    label: "Counts table",
    icon: "📋",
    description: "Get Ribo-Seq or RNA-Seq counts for genes or transcripts",
  },
  {
    id: "mrna_dist",
    label: "Origin of mapped reads",
    icon: "🎯",
    description:
      "Bar chart showing proportion of reads aligning to different regions",
  },
  {
    id: "mapped_reads_plot",
    label: "Reads breakdown",
    icon: "📉",
    description: "Breakdown of reads into multiple categories for each file",
  },
  {
    id: "unmapped",
    label: "Most frequent unmapped reads",
    icon: "❓",
    description: "Sorted table of the top 100 most frequently unmapped reads",
  },
  {
    id: "nuc_comp",
    label: "Nucleotide Composition",
    icon: "🧬",
    description: "Shows nucleotide composition across all selected reads",
  },
  {
    id: "qc_summary",
    label: "QC summary",
    icon: "✅",
    description: "Shows a summary of key data quality metrics",
  },
];

// Data Quality defaults and validation
const dataQualityDefaults = {
  // Plot type
  plottype: "",

  // Numeric settings
  minread: 20,
  maxread: 35,
  minread_count: 10,
  hmap_strt: -300,
  hmap_stop: 300,
  maxscaleval: 0,
  min_cust_metagene_search_limit: -6,
  max_cust_metagene_search_limit: 4,

  // Text settings
  custom_seq_list: "GCCRCCAUGG",
  metagene_tranlist: "",
  te_genelist: "",
  max_y_val: "",

  //Dropdown settings
  min_limit_anchor: "cds_start",
  max_limit_anchor: "cds_start",

  // Boolean settings
  log_scale: false,
  reverse: false,
  apply_offset: true,
  exclude_first: false,
  exclude_last: false,
  include_first: false,
  include_last: false,
  per: true,
  ambig: true,
  single_plt: true,
  aggregate_counts: true,

  // Radio settings
  custom_search_region: "whole_gene",
  nuc_comp_type: "per",
  direction: "fiveprime",
  nuccomp_reads: "mapped",
  center_around: "start_codons",
  color_palette: "gist_rainbow",
  norm_method: "average_read_density",
  count_type: "gene_counts",
  mrna_dist_category: "mrna_dist_region",
  corr_type: "pearson",
  count_result: "raw_count",
};

const dataQualityValidation = {
  plottype: { required: true, label: "Plot Type" },
  minread: { required: true, min: 1, max: 1000, label: "Min Read Length" },
  maxread: { required: true, min: 1, max: 1000, label: "Max Read Length" },
  max_y_val: { required: false, min: 1, max: 1000, label: "Max y value" },
  minread_count: { min: 0, label: "Min Read Count" },
  custom: (value, allSettings) => {
    if (allSettings.minread >= allSettings.maxread) {
      return "Min read length must be less than max read length";
    }
    return null;
  },
};

const DataQuality = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [loading, setLoading] = useState(true);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plotData, setPlotData] = useState(null);
  const [downloadButtonURL, setDownloadButtonURL] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [plusStatus, setPlusStatus] = useState(false);

  // Reference points to the plot container, to allow it to scroll on plot completion
  const plotContainerRef  = useRef(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
    organism: "",
    transcriptome: "",
  });

  // Enhanced hooks with reusable components
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(dataQualityDefaults, dataQualityValidation);

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  // Use the Data Quality specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useDataQualityShortCode({
      location,
      updateMultipleSettings,
      setSelectedFiles,
    });

  useEffect(() => {
    setPlotData(null);
    setDownloadButtonURL(null);
    setError(null);
  }, [settings.plottype]);

  // Reset plot-specific parameters when plot type changes
  useEffect(() => {
    const resetPlotSpecificSettings = () => {
      updateSetting("max_y_val", "");
      // Reset read length parameters for different plot types
      if (settings.plottype === "trip_periodicity") {
        updateSetting("minread", 20);
        updateSetting("maxread", 35);
      } else if (settings.plottype === "metagene_plot") {
        updateSetting("minread", 20);
        updateSetting("maxread", 35);
        updateSetting("hmap_strt", -300);
        updateSetting("hmap_stop", 300);
      } else if (settings.plottype === "heatmap") {
        updateSetting("minread", 20);
        updateSetting("maxread", 50);
        updateSetting("hmap_strt", -150);
        updateSetting("hmap_stop", 150);
      }

      // Reset other plot-specific settings
      if (settings.plottype === "metagene_plot") {
        updateSetting("apply_offset", true);
        updateSetting("aggregate_counts", true);
        updateSetting("norm_method", "average_read_density");
        updateSetting("center_around", "start_codons");
        updateSetting("direction", "fiveprime");
      } else if (settings.plottype === "custom_metagene") {
        updateSetting("apply_offset", true);
        updateSetting("aggregate_counts", true);
        updateSetting("norm_method", "none");
        updateSetting("center_around", "metagene_custom");
        updateSetting("direction", "fiveprime");
      } else if (settings.plottype === "heatmap") {
        updateSetting("apply_offset", true);
        updateSetting("log_scale", false);
        updateSetting("reverse", false);
        updateSetting("center_around", "start_codons");
        updateSetting("direction", "fiveprime");
      } else if (settings.plottype === "trip_periodicity") {
        updateSetting("single_plt", true);
      } else {
        updateSetting("minread", 20);
        updateSetting("maxread", 35);
        updateSetting("hmap_strt", -300);
        updateSetting("hmap_stop", 300);

      }

    };

    // Only reset if plot type is set and not coming from short code
    if (settings.plottype && !location.state?.suppressDefaults) {
      resetPlotSpecificSettings();
    }
  }, [
    settings.plottype,
    updateSetting,
    location.state?.isFromShortCode,
    toggleFile,
  ]);

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Check all riboseq checkboxes by default
  useDefaultFileSelection({apiData,
                           selectMultipleFiles,
                           fileType: "riboseq"})

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getDataQualityPlotData(projectId);

        const plus_status_response = await getPlusStatus(projectId);
        setPlusStatus(plus_status_response.plus_status)
        setApiData(response);

        // Note: metagene_tranlist should remain empty per project specification
        // for metagene plot defaults

        // Set default seq type with preference for riboseq
        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];

        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load project data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [
    projectId,
    updateSetting,
    setSelectedSeqType,
    setSelectedGroup,
  ]);

  // Auto-trigger plot generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot && !plotLoading && !loading && selectedFiles.length > 0) {
      console.log("Auto-triggering plot generation from short code...");
      // Set flag to false immediately to prevent multiple calls
      setAutoRunPlot(false);
      // Small delay to ensure all settings are loaded
      setTimeout(() => {
        handleGeneratePlot();
      }, 50);
    }
  }, [autoRunPlot, plotLoading, loading, selectedFiles.length, setAutoRunPlot]);

  // Auto-select first plot type on component mount
  // Only set default plot type on initial load, not when coming from short code
  useEffect(() => {
    if (
      !settings.plottype &&
      availablePlotTypes.length > 0 &&
      !location.state?.isFromShortCode
    ) {
      updateSetting("plottype", PLOT_TYPES[0].id);
    }
  }, [settings.plottype, updateSetting, location.state?.isFromShortCode]);

  // Smart read length adjustment (APPLIES TO DATA QUALITY)
  useEffect(() => {
    if (selectedFiles.length > 0 && apiData.json_files?.length > 0 && !location.state?.isFromShortCode) {
      if (
        shouldAutoAdjust("minread", settings.minread, settings.maxread) &&
        shouldAutoAdjust("maxread", settings.minread, settings.maxread)
      ) {
        const { min, max } = getStandardReadLengths(
          selectedFiles,
          apiData.json_files,
          settings.plottype
        );

        // Only update if values are different
        if (settings.minread !== min || settings.maxread !== max) {
          updateMultipleSettings({
            minread: min,
            maxread: max,
          });
          toast.info(
            `Read lengths auto-adjusted based on selected files: Min=${min}, Max=${max}`,
            { autoClose: 3000 }
          );
        }
      }
    }
  }, [
    selectedFiles,
    apiData.json_files,
    getStandardReadLengths,
    shouldAutoAdjust,
    updateMultipleSettings,
    settings.minread,
    settings.maxread,
  ]);

  // Get available plot types based on plusStatus
  const availablePlotTypes = useMemo(() => {
    if (plusStatus) {
      return PLOT_TYPES;
    }
    return PLOT_TYPES.filter(plot => plot.id !== "qc_summary");
  }, [plusStatus]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const groups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group && group !== "")
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Check if all files of selected seq type are selected
  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    // Use all files, not filtered files, and filter only by seq type
    const seqTypeFiles = (apiData.json_files || []).filter(
      (file) => file.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedSeqType, apiData.json_files, selectedFiles]);

  // Check if all files of selected group are selected
  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (file) => file.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Enhanced setting update handler with smart adjustment tracking
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      markFieldAsUserModified(key);
    },
    [updateSetting, markFieldAsUserModified]
  );

  // Enhanced reset handler
  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    setPlotData(null);
    setDownloadButtonURL(null);
    setError(null);

    // Auto-select first plot type after reset
    if (PLOT_TYPES.length > 0) {
      updateSetting("plottype", PLOT_TYPES[0].id);
    }
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetUserModifications, clearSelection, updateSetting]);

  // Handle select all seq type files
  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;

      // Use all files, not filtered files, and filter only by seq type
      const seqTypeFiles = (apiData.json_files || []).filter(
        (file) => file.file_type === selectedSeqType
      );
      const seqTypeFileIds = seqTypeFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(seqTypeFileIds);
      } else {
        deselectMultipleFiles(seqTypeFileIds);
      }
    },
    [
      selectedSeqType,
      apiData.json_files,
      selectMultipleFiles,
      deselectMultipleFiles,
    ]
  );

  // Handle select all group files
  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;

      const groupFiles = filteredFiles.filter(
        (file) => file.subgroup === selectedGroup
      );
      const groupFileIds = groupFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(groupFileIds);
      } else {
        deselectMultipleFiles(groupFileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  // Build plot request payload
  const buildPlotRequest = useCallback(() => {
    // Use the Data Quality specific payload creator
    return createPlotSpecificPayload.dataQuality({
      settings,
      selectedFiles,
      projectId,
      userPassedShortCode,
      apiData,
    });
  }, [settings, selectedFiles, projectId, apiData, userPassedShortCode]);

  // Handle generate plot
  const handleGeneratePlot = useCallback(async () => {
    if (plotLoading) {
      console.log("Plot generation already in progress, skipping...");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    if (!settings.plottype) {
      toast.error("Please select a plot type");
      return;
    }

    if (settings.plottype == "custom_metagene") {
      if (!settings.custom_seq_list) {
        toast.error("Enter at least one sequence in Custom sequence list input box")
        return;
      }
    }

    try {
      setPlotLoading(true);
      setError(null);
      setPlotData(null);
      setDownloadButtonURL(null);

      const plotRequest = buildPlotRequest();
      console.log("Requesting plot with payload: ", plotRequest);

      const response = await generateDataQualityPlot(plotRequest);
      if (response.result_rows) {
        if (response.result_rows) {
          setPlotData(response);
          toast.success("Table generated successfully");
        } else {
          throw new Error(response.message);
        }
      } else if (response.message) {
        if (response.message === "user logged out") {
          navigate("/login");
          return;
        }
        setError(response.message);
        toast.error(response.message);
      } else if (response?.plot) {
        setPlotData(response.plot);
        toast.success("Plot generated successfully");
      } else if (response?.download_button) {
        setDownloadButtonURL(response.download_button);
        toast.success("Files generated successfully");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate plot";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setPlotLoading(false);
    }
  }, [
    settings,
    selectedFiles,
    validateSettings,
    navigate,
    buildPlotRequest,
    plotLoading,
  ]);

  // Helper function to determine if plot type generates table
  const isTableType = useMemo(() => {
    const tableTypes = ["te", "unmapped"];
    return tableTypes.includes(settings.plottype);
  }, [settings.plottype]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Data Quality | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Data Quality" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Data Quality Analysis
                </h4>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={handleReset}
                  disabled={plotLoading}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Reset All
                </Button>
              </div>
            </Col>
          </Row>

          {/* Plot Type Selection */}
          <Row className="mb-4">
            <Col lg={12}>
              <PlotTypeSelector
                plotTypes={availablePlotTypes}
                selectedPlotType={settings.plottype}
                onSelectPlotType={(plotType) =>
                  updateSetting("plottype", plotType)
                }
                columnCounts={[4, 2, 4,1]}  
              />
            </Col>
          </Row>

          {/* Settings Row */}
          <Row className="mb-4">
            {/* Left: Plot Options */}
            <Col lg={12}>
              <PlotOptions
                plotType={settings.plottype}
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right: Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={groups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={true}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Files Card */}
          <Row className="mb-2">
            <Col lg={12}>
              <Files
                files={filteredFiles}
                selectedFiles={selectedFiles}
                onToggleFile={toggleFile}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                title="Select Files"
                searchable={true}
                selectable={true}
                filesPerPage={30}
                columns={6}
                showFileCount={true}
                customSearchFields={["file_name", "file_description"]}
              />
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <SelectedFilesDisplay
                selectedFiles={selectedFiles}
                allFiles={apiData.json_files}
                title="Selected Files for Data Quality"
                showSearch={true}
                maxDisplayFiles={0}
              />
            </Col>
          </Row>

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  {!settings.plottype && (
                    <span>
                      <i className="ri-information-line me-1"></i>
                      Please select a plot type to continue
                    </span>
                  )}
                  {settings.plottype && selectedFiles.length === 0 && (
                    <span>
                      <i className="ri-file-list-line me-1"></i>
                      Please select at least one file to generate a plot
                    </span>
                  )}
                  {settings.plottype && selectedFiles.length > 0 && (
                    <span>
                      <i className="ri-check-circle-line me-1 text-success"></i>
                      Ready to generate with{" "}
                      <strong>{selectedFiles.length}</strong> file
                      {selectedFiles.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                <ActionButton
                  onClick={handleGeneratePlot}
                  loading={plotLoading}
                  buttonType={isTableType ? "table" : "plot"}
                />
              </div>
            </Col>
          </Row>

          {/* Download button container for non-aggregated counts */}
          {(downloadButtonURL) && (
            <div style={{ paddingLeft: 55, paddingTop: 22, display: "flex", justifyContent:"center"}}>
              <a
                style={{color:"white", fontSize: 18, backgroundColor: "#76bd7b", padding: 8, borderRadius: 6}}
                href={downloadButtonURL}
                target="_blank"
                rel="noopener noreferrer"
                className="button centerbutton"
              >
                <i class="ri-download-line me-1"></i>
                <b>Download Results</b>
              </a>
            </div>
          )}

          {/* Plot Container */}
          {(plotData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                {settings.plottype === "qc_summary" ? (
                        <QCSummaryDashboard 
                          data={plotData} 
                          loading={plotLoading} 
                          error={error} 
                          onRetry={handleGeneratePlot}
                        />
                      ) : (
                        <PlotContainer
                          ref={plotContainerRef}
                          plotData={plotData}
                          loading={plotLoading}
                          error={error}
                          selectedFilesCount={selectedFiles.length}
                          title="Data Quality Results"
                          onRetry={handleGeneratePlot}
                          showFileCount={true}
                          isTable={isTableType}
                        />
                      )}
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DataQuality;
